import CheckIcon from "@mui/icons-material/Check";
import FilterListIcon from "@mui/icons-material/FilterList";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Popover from "@mui/material/Popover";
import { FormInputProps } from "_interfaces/_common/forms";
import CustomDateRangePicker from "component/_common/forms/customDateRangePicker";
import DatePicker from "component/_common/forms/date";
import InputAutoComplete from "component/_common/forms/inputAutoComplete";
import InputText from "component/_common/forms/inputText";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { Fragment } from "react/jsx-runtime";

interface Props {
  filters: any;
  setFilters: React.Dispatch<React.SetStateAction<any>>;
  filterList: FormInputProps[];
  handleRefresh: () => void;
}

export const CustomFilter: React.FC<Props> = ({
  filters,
  setFilters,
  filterList,
  handleRefresh,
}) => {
  const [tempFilters, setTempFilters] = useState<any>({});

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [hasPopoverOpened, setHasPopoverOpened] = useState(false);

  const open = Boolean(anchorEl);
  const id = open ? "filter-popover" : undefined;

  const handleAutoComplete = (
    e: React.SyntheticEvent,
    value: any,
    name: string,
    multiple?: boolean
  ) => {
    setTempFilters((prev: any) => ({
      ...prev,
      [name]: multiple
        ? value?.map(
            (e: { value: string | number; id: string | number }) =>
              e?.value || e?.id
          )
        : value?.value || value?.id,
    }));
  };

  const handleNumber = (e: React.SyntheticEvent, value: any, name: string) => {
    setTempFilters((prev: any) => ({
      ...prev,
      [name]: value ? parseInt(value) : null,
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setTempFilters((prev: any) => ({ ...prev, [name]: checked }));
    } else {
      setTempFilters((prev: any) => ({ ...prev, [name]: value }));
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);

    setTempFilters({ ...filters });

    if (!hasPopoverOpened) {
      setHasPopoverOpened(true);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const applyFilters = () => {
    setFilters(tempFilters);
    setIsFilterApplied(true);
    handleRefresh();
    handleClose();
  };

  const clearAllFilters = () => {
    const cleared: Record<string, any> = {};

    // Clear filters defined in filterList
    filterList.forEach((filter) => {
      cleared[filter.name] = filter.multiple ? [] : undefined;
    });

    // ✅ Also clear date-related filters (even if not in filterList)
    const dateFields = ["fromDate", "toDate", "dateRange"];
    dateFields.forEach((dateField) => {
      if (tempFilters.hasOwnProperty(dateField)) {
        cleared[dateField] = undefined;
      }
    });

    // ✅ Check if there’s any change
    const isFiltersChanged = Object.keys(cleared).some(
      (key) => tempFilters[key] !== cleared[key]
    );

    if (isFiltersChanged) {
      setTempFilters(cleared);
      setFilters(cleared);
      handleRefresh();
      handleClose();
      setIsFilterApplied(false);
    }
  };

  const getActiveFilterCount = (filters: any, filterList: any[]) => {
    let countedDateRange = false;

    return filterList.reduce((count, filter) => {
      const name = filter.name;
      const value = filters?.[name];

      // Handle date range (count fromDate + toDate as one)
      if (["fromDate", "toDate", "dateRange"].includes(name)) {
        if (!countedDateRange) {
          const from = filters?.fromDate;
          const to = filters?.toDate;

          const hasDateRange = (from && from !== "") || (to && to !== "");
          if (hasDateRange) count += 1;

          countedDateRange = true;
        }
        return count;
      }

      const isEmpty =
        value === undefined ||
        value === null ||
        (typeof value === "string" && value.trim() === "") ||
        (Array.isArray(value) && value.length === 0);

      return isEmpty ? count : count + 1;
    }, 0);
  };

  // Active filter count (memoized)
  const activeFilterCount = useMemo(
    () => getActiveFilterCount(filters, filterList),
    [filters, filterList]
  );

  // Active filter count (memoized)
  const activeTempFilterCount = useMemo(
    () => getActiveFilterCount(tempFilters, filterList),
    [tempFilters, filterList]
  );

  // 🔹 Set isFilterApplied once on mount if any filter exists
  useEffect(() => {
    if (!hasPopoverOpened) {
      if (activeFilterCount > 0) {
        setIsFilterApplied(true);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilterCount, hasPopoverOpened]);

  const getPopoverMaxWidth = (filterCount: number) => {
    if (filterCount <= 2) return 400;
    if (filterCount <= 6) return 600;
    if (filterCount <= 9) return 800;
    return 1100;
  };

  const getGridCols = (filterCount: number) => {
    if (filterCount <= 2) return 12;
    if (filterCount <= 6) return 6;
    if (filterCount <= 9) return 4;
    return 3;
  };

  return (
    <>
      <Badge
        badgeContent={activeFilterCount}
        color="primary"
        invisible={activeFilterCount === 0 || !isFilterApplied}
      >
        <Button
          aria-describedby={id}
          variant="outlined"
          onClick={handleClick}
          startIcon={<FilterListIcon />}
        >
          Filters
        </Button>
      </Badge>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box
          p={2}
          sx={{
            maxWidth: getPopoverMaxWidth(filterList.length),
            width: "90vw",
          }}
        >
          <Grid container spacing={1}>
            {filterList.map(
              (
                {
                  type,
                  name,
                  label,
                  placeholder,
                  multiple,
                  enabled = true,
                  options,
                  setOptions,
                  fetchOptions,
                  serverPaginated,
                  pageSize,
                  refreshTrigger,
                },
                index
              ) =>
                enabled && (
                  <Fragment key={index}>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={getGridCols(filterList.length)}
                    >
                      {type === "autoComplete" ? (
                        <InputAutoComplete
                          name={name}
                          label={label}
                          placeholder={placeholder}
                          onChange={(e, v, m) =>
                            handleAutoComplete(e, v, name, m)
                          }
                          value={
                            options && options?.length && tempFilters?.[name]
                          }
                          multiple={multiple}
                          options={options || []}
                          setOptions={setOptions}
                          fetchOptions={fetchOptions}
                          serverPaginated={serverPaginated}
                          pageSize={pageSize}
                          refreshTrigger={refreshTrigger}
                        />
                      ) : type === "date" ? (
                        <DatePicker
                          name={name}
                          label={label}
                          placeholder={placeholder}
                          value={tempFilters[name]}
                          onChange={(value) => {
                            setTempFilters((prev: any) => ({
                              ...prev,
                              [name]: value,
                            }));
                          }}
                        />
                      ) : type === "dateRange" ? (
                        <CustomDateRangePicker
                          label={label}
                          value={[
                            tempFilters?.fromDate
                              ? moment(tempFilters.fromDate).toDate()
                              : null,
                            tempFilters?.toDate
                              ? moment(tempFilters.toDate).toDate()
                              : null,
                          ]}
                          onChange={(newValue: [Date | null, Date | null]) => {
                            setTempFilters((prev: any) => ({
                              ...prev,
                              fromDate: newValue[0]
                                ? moment(newValue[0]).format("YYYY/MM/DD")
                                : null,
                              toDate: newValue[1]
                                ? moment(newValue[1]).format("YYYY/MM/DD")
                                : null,
                            }));
                          }}
                        />
                      ) : type === "number" ? (
                        <Grid item xs={12}>
                          <InputText
                            type={"number"}
                            name={name}
                            label={label}
                            placeholder={placeholder}
                            value={tempFilters[name]}
                            onChange={(e) =>
                              handleNumber(e, e.target.value, name)
                            }
                          />
                        </Grid>
                      ) : type === "text" || type === "checkbox" ? (
                        <InputText
                          type={type}
                          name={name}
                          label={label}
                          placeholder={placeholder}
                          autoComplete={"off"}
                          value={tempFilters[name]}
                          onChange={handleChange}
                        />
                      ) : null}
                    </Grid>
                  </Fragment>
                )
            )}
          </Grid>

          <Box
            pt={2}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              justifyContent:
                activeTempFilterCount || activeFilterCount
                  ? "space-between"
                  : "flex-end",
            }}
          >
            {activeTempFilterCount || activeFilterCount ? (
              <Button
                variant="outlined"
                color="error"
                size="small"
                sx={{
                  fontSize: 14,
                }}
                startIcon={<FilterListOffIcon />}
                onClick={clearAllFilters}
              >
                Clear All Filters
              </Button>
            ) : null}
            <Button
              variant="contained"
              color="primary"
              size="small"
              sx={{
                fontSize: 14,
              }}
              endIcon={<CheckIcon />}
              disabled={!activeTempFilterCount && !activeFilterCount}
              onClick={applyFilters}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};
